import {
  computed,
  signal,
  type Signal,
  type WritableSignal,
} from '@angular/core';
import {
  APP_SESSION,
  type AppSession,
  type Company,
  createMockCompany,
  MOCK_USER,
  resolveUserTeamRole,
  type TeamRole,
  type User,
} from '@indutrust/shared-model';
import { vi } from 'vitest';

/**
 * Default mock {@link Company} used by {@link provideAppSessionMock} when no
 * override is supplied. Built from the canonical fixture so MSW handlers
 * and unit tests stay in sync.
 */
const DEFAULT_MOCK_APP_SESSION_COMPANY: Company = createMockCompany({
  name: 'Test Company',
});

/**
 * Options accepted by {@link provideAppSessionMock}.
 */
export type AppSessionMockOptions = {
  user?: User | null;
  company?: Company | null;
  isAuthenticated?: boolean;
  teamRole?: TeamRole;
};

/**
 * Provider factory that registers a mock {@link AppSession} under the
 * {@link APP_SESSION} injection token for tests.
 *
 * Defaults to an authenticated admin session using `MOCK_USER` (from
 * `@indutrust/shared-model`) and a built-in mock {@link Company}. Override
 * any field through the `opts` argument.
 *
 * Prefer this helper over injecting and mutating a concrete `AuthSessionStore`
 * so that `scope:shared` specs do not depend on `@indutrust/auth-data`.
 *
 * @example
 * beforeEach(async () => {
 *   await TestBed.configureTestingModule({
 *     imports: [MyComponent, getTranslocoTestingModule()],
 *     providers: [provideAppSessionMock()],
 *   }).compileComponents();
 * });
 * @example
 * // Supplier user with a custom company
 * provideAppSessionMock({
 *   user: createMockUser({ type: 'supplier' }),
 *   company: { id: 42, name: 'Acme', status: 'paid', type: 'supplier', createdAt: '' },
 * });
 */
export function provideAppSessionMock(opts: AppSessionMockOptions = {}) {
  const userSignal = signal(opts.user ?? MOCK_USER);
  // The intersection is what makes `asMockAppSession` sound: it proves at compile
  // time that the session's signals really are writable.
  const mock: AppSession & MockAppSessionControls = {
    user: userSignal,
    company: signal(opts.company ?? DEFAULT_MOCK_APP_SESSION_COMPANY),
    isAuthenticated: signal(opts.isAuthenticated ?? true),
    teamRole:
      opts.teamRole === undefined
        ? computed(() => resolveUserTeamRole(userSignal()))
        : signal(opts.teamRole),
    logout: vi.fn<() => void>(),
  };

  return { provide: APP_SESSION, useValue: mock };
}

/**
 * Convenience shortcut for {@link provideAppSessionMock} that registers an
 * unauthenticated session (no user, no company, `isAuthenticated === false`).
 *
 * @example
 * providers: [provideUnauthenticatedAppSessionMock()],
 */
export function provideUnauthenticatedAppSessionMock() {
  return provideAppSessionMock({
    user: null,
    company: null,
    isAuthenticated: false,
  });
}

/** Unwraps the value carried by a signal-typed {@link AppSession} member. */
type SessionSignalValue<T> = T extends Signal<infer V> ? V : never;

/**
 * The writable signals behind {@link provideAppSessionMock}.
 *
 * Value types are derived from {@link AppSession} so the two cannot drift.
 *
 * `teamRole` is deliberately absent — {@link provideAppSessionMock} derives it
 * with `computed()` unless an explicit role was supplied, so it is not reliably
 * writable.
 */
export type MockAppSessionControls = {
  company: WritableSignal<SessionSignalValue<AppSession['company']>>;
  isAuthenticated: WritableSignal<
    SessionSignalValue<AppSession['isAuthenticated']>
  >;
  user: WritableSignal<SessionSignalValue<AppSession['user']>>;
};

/**
 * Re-types an {@link AppSession} obtained from {@link provideAppSessionMock} so a
 * spec can flip session state mid-test — e.g. asserting that an effect fires
 * when the session becomes authenticated.
 *
 * {@link AppSession} exposes read-only `Signal`s on purpose so production code
 * cannot mutate the session; {@link provideAppSessionMock} backs them with
 * writable signals. `APP_SESSION` erases that, so recovering it needs one
 * assertion — kept here rather than repeated in every spec. `WritableSignal`
 * carries a brand symbol, so the widening has to go through `unknown`.
 *
 * Only valid for sessions registered by {@link provideAppSessionMock} — a real
 * `AuthSessionStore`-backed session is genuinely read-only. Reach for
 * {@link setupAuthenticatedSession} in that case.
 *
 * @example
 * const session = asMockAppSession(TestBed.inject(APP_SESSION));
 * session.isAuthenticated.set(true);
 * TestBed.flushEffects();
 */
export function asMockAppSession(session: AppSession): MockAppSessionControls {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- provideAppSessionMock builds the object as AppSession & MockAppSessionControls
  return session as unknown as MockAppSessionControls;
}
